import { Injectable, Injector, Predicate, computed, inject, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import {
  Airline,
  CatalogueGetFlightDateOptionsListParams,
  City,
  CityGroupByCountry,
  Country,
  Hotel,
  HotelServiceEnum,
  HotelTypeEnum,
  SearchTourPackagesRequest
} from '@appTypes/api.types';
import { SearchDateModes } from '@appTypes/common.types';
import { DistrictsTreeItem } from '@appTypes/redefined-types';
import { convertLocationsIntValuesToValues, deepClean, generateNumbersRange } from '@core/misc/misc.utils';
import { pickDestinationCountry } from '@core/misc/destination-country.utils';
import { SEARCH_LINK_ADJUSTED_MESSAGE, clampSearchLinkParty, warnSearchLink } from '@core/misc/search-link.utils';
import { flattenDepartureCities, pickDepartureCity } from '@core/misc/departure-city.utils';
import {
  greaterThanOrEquals,
  lessThanOrEquals,
  maxDateThanField,
  minDate,
  minDateThanField,
  minNumbers
} from '@core/misc/validators.utils';
import { RequestStateModel } from '@core/store/store.models';
import { SVGIcon, SVG_ICONS } from '@data/svg-icons.data';
import { environment } from '@env/environment';
import { UpdateFormValue } from '@ngxs/form-plugin';
import { Store, createDispatchMap, createSelectMap } from '@ngxs/store';
import {
  CHILDRAN_AGES,
  PAGINATION_LEVELS,
  STATIC_OPTIONS_HOTEL_CLASSES_STARS,
  STATIC_OPTIONS_HOTEL_MEAL_PLANS,
  STATIC_OPTONS_HOTEL_SERVICES,
  STATIC_OPTONS_HOTEL_TYPES
} from '@pages/_common/static-data.const';
import {
  toursGetArrivalDistricts,
  toursGetAvailableAirlines,
  toursGetDestinationCountries,
  toursGetHotels,
  toursLoadFlightDateOptions,
  toursResetSearchResult,
  toursSearchTourPackages
} from '@pages/tours-page/state/tours.actions';
import { ToursState } from '@pages/tours-page/state/tours.state';
import {
  NGXS_TOURS_SEARCH_FORM,
  TOUR_DIRECT_FLIGHT_CHECKBOX_HIDE_FOR_ARRIVAL_DIRECTIONS
} from '@pages/tours-page/tours-page.const';
import { DynamicDateClassItem, DynamicDateClassLoadFn } from '@shared/components/dates/dynamic-date-picker.abstract';
import { LoadingWindowService } from '@shared/components/loading-window/loading-window.service';
import { ConfirmService } from '@shared/modules/confirm/confirm.service';
import { addDays, differenceInDays, format } from 'date-fns';
import { Observable, combineLatest, filter, lastValueFrom, map, switchMap, take } from 'rxjs';
import {
  appClearSearchByUrl,
  appGetDepartureCities,
  appResultIsFromGeneratedUrl,
  appSetCurrency,
  appSetSellCurrency
} from '@app-state/app.actions';
import { AppPersistedState } from '../../../state/app.persisted.state';
import { AppState } from '../../../state/app.state';
import { airlineLogoIcon } from '@core/services/airline-logos.service';

@Injectable()
export class ToursSearchFormService {
  //Injectors
  readonly #injector = inject(Injector);
  readonly #loadingWindowService = inject(LoadingWindowService);
  readonly #store = inject(Store);
  readonly #confirmService = inject(ConfirmService);

  //Definitions
  readonly today = new Date(new Date().setHours(0, 0, 0, 0));
  readonly ngxsForm = NGXS_TOURS_SEARCH_FORM;
  readonly searchByPeriodNightsOptions = generateNumbersRange(2, 7);
  readonly pages = PAGINATION_LEVELS;
  readonly childrenAgesData = CHILDRAN_AGES;
  readonly moveCheckinDaysByCountryCode = environment.toursMoveCheckinDaysByCountryCode;
  //Writable Values
  flightAvailabilityByParamsIdentifier!: string;
  isInit = false;
  isSearchByModule = false;
  isReady = false;

  //signals
  readonly isSubmited = signal(false);
  readonly searchMode = signal<SearchDateModes | null>(null);
  readonly hideDirectFlightsOnly = signal(false);

  public readonly params = {
    minAdults: 1,
    maxAdults: 10,
    minChildren: 0,
    maxChildren: 10,
    minChildAge: 0,
    maxChildAge: environment.childMaxAge, // online-api#67: 17, not 18
    minNightsCount: 1,
    maxNightsCount: 30
  };
  //Selectors
  readonly #selectors = createSelectMap({
    formStatus: ToursState.searchLoadStatus,
    firstBatchReady: ToursState.searchFirstBatchReady,
    isResult: ToursState.isResult,
    activedCountry: AppPersistedState.activedCountry,
    defaultDepartureCityUID: AppPersistedState.defaultDepartureCityUID,
    activedDestinationCountry: AppPersistedState.activedDestinationCountry,
    searchFormData: ToursState.searchFormData,
    searchByUrlData: AppState.searchByUrlData('tours'),
    destinationCountries: ToursState.destinationCountries,
    destinationDefaultCountry: ToursState.destinationDefaultCountry,
    destinationDefaultDepartureCityUID: ToursState.destinationDefaultDepartureCityUID,
    departureCities: AppState.departureCities,
    availableAirlines: ToursState.availableAirlines,
    arrivalDistricts: ToursState.arrivalDistricts,
    hotels: ToursState.hotels,
    currencies: AppState.currencies,
    activedCurrency: AppPersistedState.activedCurrency,
    isGeneratedURL: AppState.isGeneratedURL,
    flightAvailabilityByParams: ToursState.flightAvailabilityByParams(this.flightAvailabilityByParamsIdentifier)
  });

  //Shortcuts
  get formStatus() {
    return this.#selectors.formStatus;
  }
  get isResult() {
    return this.#selectors.isResult;
  }

  //Writable Values
  storeArrivalCountryCode: string | null = this.#selectors.activedDestinationCountry();
  storeDepartureCityUID: string | null = this.#selectors.defaultDepartureCityUID();

  //Actions
  readonly #actions = createDispatchMap({
    updateFormValue: UpdateFormValue,
    searchTourPackages: toursSearchTourPackages,
    resultIsFromGeneratedUrl: appResultIsFromGeneratedUrl,
    clearSearchByUrl: appClearSearchByUrl,
    getDestinationCountries: toursGetDestinationCountries,
    getDepartureCities: appGetDepartureCities,
    getAvailableAirlines: toursGetAvailableAirlines,
    getArrivalDistricts: toursGetArrivalDistricts,
    getHotels: toursGetHotels,
    setCurrency: appSetCurrency,
    setSellCurrency: appSetSellCurrency,
    resetSearchResult: toursResetSearchResult,
    loadFlightDateOptions: toursLoadFlightDateOptions
  });

  //Form Group
  readonly formGroup = new FormGroup({
    departureCountryCode: new FormControl<string | null>(this.#selectors.activedCountry()),
    departureCityUID: new FormControl<string | null>(this.#selectors.defaultDepartureCityUID()),
    airlineCode: new FormControl<string | null>(null),
    arrivalCountryCode: new FormControl<string | null>(this.#selectors.activedDestinationCountry(), [
      Validators.required
    ]),
    adults: new FormControl<number>(2, [Validators.min(this.params.minAdults), Validators.max(this.params.maxAdults)]),
    children: new FormControl<number>(0, [
      Validators.min(this.params.minChildren),
      Validators.max(this.params.maxChildren)
    ]),
    childrenAges: new FormArray<FormControl<number | null>>([]),

    departureDate: new FormControl<Date>(this.today, [Validators.required, minDate(this.today)]),
    departureDateTo: new FormControl<Date | null>(null),
    returnDate: new FormControl<Date>(addDays(this.today, 7)),
    departureAndReturnDaysCount: new FormControl<number>(7, [
      Validators.min(this.params.minNightsCount),
      Validators.max(this.params.maxNightsCount)
    ]),
    nights: new FormControl<number | null>(null),
    nightsTo: new FormControl<number | null>(null),
    checkInDate: new FormControl<Date>(this.today, [
      Validators.required,
      minDateThanField(
        'departureDate',
        'The difference between the flight departure date and the hotel check-in date may not exceed one day.',
        -1
      ),
      maxDateThanField(
        'departureDate',
        'The difference between the flight departure date and the hotel check-in date may not exceed one day.',
        1
      )
    ]),
    checkInDateTo: new FormControl<Date | null>(null, minDateThanField('checkInDate')),
    checkOutDate: new FormControl<Date>(addDays(this.today, 7), [Validators.required, minDateThanField('checkInDate')]),
    checkInCheckOutChange: new FormControl<boolean>(false),
    checkInAndCheckOutDaysCount: new FormControl<number>(7, [
      minNumbers(this.params.minNightsCount, `Number of nights must be greater than {minNumbers}`),
      Validators.max(this.params.maxNightsCount)
    ]),
    directFlightsOnly: new FormControl<boolean | null>(true),
    longConnectTime: new FormControl<boolean | null>(null),
    extendedSearch: new FormControl<boolean | null>(null),
    freeSale: new FormControl<boolean | null>(null),
    groupByHotel: new FormControl<boolean | null>(null),
    groupByHotelId: new FormControl<boolean | null>(null),

    arrivalLocations: new FormControl<DistrictsTreeItem[] | null>(null),
    hotelCodes: new FormControl<string[] | null>(null),

    totalPriceFrom: new FormControl<number | null>(null, [lessThanOrEquals('totalPriceTo')]),
    totalPriceTo: new FormControl<number | null>(null, [greaterThanOrEquals('totalPriceFrom')]),
    hotelTypes: new FormControl<HotelTypeEnum[] | null>(null), //HotelTypeEnum[] | null;
    hotelClasses: new FormControl<string[] | null>(null),
    hotelServices: new FormControl<HotelServiceEnum[] | null>(null),

    mealPlans: new FormControl<string[] | null>(null),

    districtUIDs: new FormControl(), //??
    onlyAvailableRooms: new FormControl<boolean | null>(null),
    searchCurrency: new FormControl<string | null>(null),
    //
    ticketType: new FormControl<string | null>(null),
    //Int method
    hotelIds: new FormControl<number[] | null>(null),
    arrivalCountryId: new FormControl<number | null>(null),
    departureCountryId: new FormControl<number | null>(null),
    departureCityID: new FormControl<number | null>(null)
  });

  //FormValues (Signals)
  readonly formValues = {
    adults: toSignal(this.formGroup.controls.adults.valueChanges.pipe(map((e) => e ?? 0))),
    children: toSignal(this.formGroup.controls.children.valueChanges.pipe(map((e) => e ?? 0))),
    checkInCheckOutChange: toSignal(this.formGroup.controls.checkInCheckOutChange.valueChanges),
    departureDate: toSignal(this.formGroup.controls.departureDate.valueChanges),
    returnDate: toSignal(this.formGroup.controls.returnDate.valueChanges),
    checkInDate: toSignal(this.formGroup.controls.checkInDate.valueChanges),
    mealPlans: toSignal(this.formGroup.controls.mealPlans.valueChanges.pipe(map((e) => e?.join(',')))),
    arrivalCountryCode: toSignal(this.formGroup.controls.arrivalCountryCode.valueChanges),
    airlineCode: toSignal(this.formGroup.controls.airlineCode.valueChanges)
  };

  readonly validationValues = {
    minReturnDate: toSignal(
      this.formGroup.controls.departureDate.valueChanges.pipe(
        map((e) => addDays(e ?? this.today, this.params.minNightsCount))
      )
    ),
    minCheckOutDate: toSignal(
      this.formGroup.controls.checkInDate.valueChanges.pipe(
        map((e) => addDays(e ?? this.today, this.params.minNightsCount))
      )
    )
  };

  //Shortcuts
  get childrenAges() {
    return this.formGroup.controls.childrenAges.controls;
  }
  get hasChildrenAgesError() {
    if (!this.formGroup.controls.childrenAges.controls.length) return false;
    return this.formGroup.controls.childrenAges.controls.some((e) => e.errors);
  }
  get hasLimitErrorOnDepartureAndReturnDaysCount() {
    return Boolean(this.formGroup.controls.departureAndReturnDaysCount.errors?.['max']);
  }
  get hasLimitErrorOndepartureCheckInAndCheckOutDaysCount() {
    return Boolean(this.formGroup.controls.checkInAndCheckOutDaysCount.errors?.['max']);
  }
  //Countries Selectotor helpers
  readonly countriesTypedToken!: Country;
  readonly countries = this.#selectors.destinationCountries;
  readonly countriesIconGetter = (c?: Country) => {
    if (c?.countryCode === null) return 'map-marker';
    const icon = c?.countryCode ? (`flag-${c.countryCode.toLowerCase()}` as SVGIcon) : 'map-marker';
    return SVG_ICONS?.[icon] ? icon : 'map-marker';
  };

  readonly arrivalCountriesFilterPredicate = signal<Predicate<Country> | null>(null);

  //Cities Selectotor helpers
  readonly citiesTypedToken!: City & { cities: City[] };
  readonly departureCities = this.#selectors.departureCities;
  readonly citiesExtraOptions: CityGroupByCountry[] = [];
  readonly departureCityIconGetter = (c?: CityGroupByCountry & City) =>
    c?.countryCode ? (`flag-${c.countryCode.toLowerCase()}` as SVGIcon) : null;
  /** DEST-017: `data-testid` per departure-city option (C16); a country group row has no `cityUID`. */
  readonly departureCityOptionTestId = (c: CityGroupByCountry & City) =>
    c?.cityUID ? `tours-departure-city-option-${c.cityUID}` : 'tours-departure-city-option-group';
  /** DEST-017: the account has no departure city set up at all (distinct from a load error - empty is a valid answer). */
  readonly departureCitiesEmpty = computed(
    () =>
      this.departureCities().loadState.status === 'completed' &&
      !flattenDepartureCities(this.departureCities().response ?? []).length
  );

  //Airlines Selectotor helpers
  readonly airlinesTypedToken!: Airline;
  readonly airlines = this.#selectors.availableAirlines;
  /** R-FC38 (flight-catalogue qa.md): each airline's logo from NugiosCore; "All airlines" = the neutral icon. */
  readonly airlinesIconGetter = (a?: Airline): SVGIcon => airlineLogoIcon(a?.airlineCode);
  readonly airlinesExtraOptions: Airline[] = [{ airlineCode: null, airlineName: 'All Airlines' }];

  //District Selector Helpers
  readonly arrivalDistrictsTypedToken!: DistrictsTreeItem;
  get arrivalDistricts() {
    return this.#selectors.arrivalDistricts;
  }

  //Currency Selectotor helpers
  get activedCurrency() {
    return this.#selectors.activedCurrency;
  }
  get currencies() {
    return this.#selectors.currencies;
  }
  get onCurrencySelect() {
    return this.#actions.setCurrency;
  }
  //Hotels Selector Helpers
  readonly availableHotelsTypedToken!: Hotel;
  get availableHotels() {
    return this.#selectors.hotels;
  }
  readonly hotelsOptionFilterPredicate = signal<Predicate<Hotel> | null>(null);

  //Misc Selector Options
  readonly mealPlansOptions = STATIC_OPTIONS_HOTEL_MEAL_PLANS;
  readonly hotelClassesOptions = STATIC_OPTIONS_HOTEL_CLASSES_STARS;
  readonly hotelServicesOptions = STATIC_OPTONS_HOTEL_SERVICES;
  readonly hotelTypesOptions = STATIC_OPTONS_HOTEL_TYPES;

  //Utils
  periodDepartureDateToFilterFn = (d: Date | null): boolean => {
    const departureDate = this.formGroup.controls.departureDate.value;
    if (departureDate && d) {
      const diff = differenceInDays(d, departureDate);
      if (diff >= 0 && diff <= 7) {
        return true;
      }
    }
    return false;
  };
  periodCheckInDateToFilterFn = (d: Date | null): boolean => {
    const checkInDate = this.formGroup.controls.checkInDate.value;
    const nightsTo = this.formGroup.controls.nightsTo.value ?? 2;
    if (checkInDate && d) {
      const diff = differenceInDays(d, checkInDate);
      if (diff >= 0 && diff <= nightsTo) {
        return true;
      }
    }
    return false;
  };

  init() {
    if (this.isInit) return;

    this.initSearchData();
    this.updateCheckOutCheckInControls();
    this.isInit = true;
  }
  onSubmit(showLoader = true) {
    if (!this.isSubmited()) this.isSubmited.set(true);

    if (this.formGroup.invalid) return;

    this.#actions.searchTourPackages();
    if (this.#selectors.isGeneratedURL()) this.#actions.resultIsFromGeneratedUrl(false);
    if (showLoader) {
      this.#loadingWindowService.show();
      // R-PT2: the loader stays until the first tours are listed (or the search completed or failed), not merely until the first answer
      toObservable(this.#selectors.firstBatchReady, { injector: this.#injector })
        .pipe(filter(Boolean), take(1))
        .subscribe(() => this.#loadingWindowService.hide());
    }
  }
  updateArrivalCountryValue(city: City | null) {
    if (city?.countryCode) {
      this.formGroup.controls.departureCountryCode.setValue(city.countryCode);
      //
      const restrictionCountryInternalReservations = environment.restrictionCountryInternalReservations;
      if (typeof restrictionCountryInternalReservations === 'undefined') return;
      if (restrictionCountryInternalReservations.includes(city.countryCode)) {
        if (this.formGroup.controls.arrivalCountryCode.value === city.countryCode) {
          const activedDestinationCountry = this.#selectors.activedDestinationCountry();
          if (activedDestinationCountry !== city.countryCode)
            this.formGroup.controls.arrivalCountryCode.setValue(activedDestinationCountry);
          else this.formGroup.controls.arrivalCountryCode.setValue(null); //
        }

        this.arrivalCountriesFilterPredicate.update(
          () => (country: Country) => country.countryCode !== city.countryCode
        );
      } else {
        this.arrivalCountriesFilterPredicate.update(() => null);
      }
    }
  }

  onAddChildren(count: number) {
    // R-BIH42 (website-web#84): never below 0 - removeAt on an empty array does nothing, so a negative count looped forever and hung the tab
    const children = Math.max(count ?? 0, 0);
    const current = this.formGroup.controls.childrenAges.length;
    if (current < children)
      for (let index = 0; index < children - current; index++)
        this.formGroup.controls.childrenAges.push(new FormControl<number | null>(null, Validators.required));
    else if (current > children)
      // R-HP-R3 (website-web#33): remove from the end - a climbing index over a shrinking array left stale age fields
      while (this.formGroup.controls.childrenAges.length > children)
        this.formGroup.controls.childrenAges.removeAt(this.formGroup.controls.childrenAges.length - 1);
  }

  updateHotelFilters(isInitialRequest = false) {
    if (!isInitialRequest && !this.isReady) return;
    const { hotelClasses, hotelTypes, arrivalLocations } = this.formGroup.value;

    const cityIds = arrivalLocations?.filter((el) => el.type === 'city').map((el) => el.value);
    const districtIds = arrivalLocations?.filter((el) => el.type === 'district').map((el) => el.value);
    if (!isInitialRequest) this.formGroup.controls.hotelCodes.setValue(null);

    if (!hotelClasses && !hotelTypes && !arrivalLocations && !cityIds && !districtIds) {
      if (this.hotelsOptionFilterPredicate()) this.hotelsOptionFilterPredicate.update(() => null);
      return;
    }

    this.hotelsOptionFilterPredicate.update(
      () => (hotel: Hotel) =>
        (!hotelClasses?.length || hotelClasses.includes(hotel.hotelClass as string)) &&
        (!hotelTypes?.length || hotelTypes.includes(hotel.hotelType as HotelTypeEnum)) &&
        (!cityIds?.length || cityIds.includes(hotel.cityUID)) &&
        (!districtIds?.length || districtIds.includes(hotel.districtUID))
    );
  }

  /**
   * Amiran, 23.09.2026: the search form opened on a country the site no longer offered (the one remembered in the browser, first time
   * the built-in default) and the country chooser showed nothing. Once the offered countries are in, the form keeps its arrival country
   * only if it is offered; otherwise the API's default, else the first offered (pickDestinationCountry), with its selling currency.
   */
  async #settleDestinationCountry() {
    const offered = await lastValueFrom(
      toObservable(this.countries, { injector: this.#injector }).pipe(
        filter((e) => !!e.loadState.status && e.loadState.status !== 'loading'),
        take(1)
      )
    );
    const current = this.formGroup.controls.arrivalCountryCode.value;
    const code = pickDestinationCountry(current, offered.response, this.#selectors.destinationDefaultCountry());
    if (!code) return;
    if (code !== current) {
      this.formGroup.controls.arrivalCountryCode.setValue(code);
      this.storeArrivalCountryCode = code;
    }
    // website-web#90: the selling currency is applied also when the form already held this country (a fresh session's default) -
    // returning early left the header listing every currency instead of the destination's own. appSetSellCurrency is a no-op when
    // nothing changed and keeps the agent's own currency while the destination offers it.
    const sellCurrency = offered.response?.find((c) => c.countryCode === code)?.sellCurrency;
    if (sellCurrency) this.#actions.setSellCurrency('tours', sellCurrency);
  }

  /**
   * DEST-017: the departure city the search form opens with - the URL/saved-search value if it is one of the cities
   * offered, else the Destination Module's default for this agent, else the first offered. Returns `undefined` when
   * no departure city is set up for the account (nothing to reload the destinations with).
   */
  async #settleDepartureCity(): Promise<string | undefined> {
    const citiesState = await lastValueFrom(
      toObservable(this.departureCities, { injector: this.#injector }).pipe(
        filter((e) => !!e.loadState.status && e.loadState.status !== 'loading'),
        take(1)
      )
    );
    const flatCities = flattenDepartureCities(citiesState.response ?? []);
    if (!flatCities.length) return undefined;

    // the API default lives on the destination-countries response (same initial, cityless request) - wait for it too
    await lastValueFrom(
      toObservable(this.countries, { injector: this.#injector }).pipe(
        filter((e) => !!e.loadState.status && e.loadState.status !== 'loading'),
        take(1)
      )
    );

    const current = this.formGroup.controls.departureCityUID.value;
    const chosen = pickDepartureCity(current, flatCities, this.#selectors.destinationDefaultDepartureCityUID());
    if (!chosen) return undefined;

    // online-api#62 (run 26 WEB-26-13): the city is the settled one before the form shows it, so the select writing it back is not a change -
    // the caller reloads its destinations once (it was asked twice and the first call aborted)
    this.storeDepartureCityUID = chosen;
    if (chosen !== current) this.formGroup.controls.departureCityUID.setValue(chosen);
    return chosen;
  }

  /** DEST-017: the destinations sold from `cityUid` (package mode); settles the arrival country against that narrower list. */
  #reloadDestinationCountriesForDepartureCity(cityUid?: string) {
    this.#actions.getDestinationCountries(cityUid, true);
    return this.#settleDestinationCountry();
  }

  /**
   * The Retry action of the departure-city empty/error state (DEST-017). A successful retry settles the departure
   * city exactly as init does (the URL/saved-search value, else the Destination Module's default, else the first
   * offered) and reloads the destinations sold from it - once, for this retry's own result, never on a later load.
   */
  /** C17: the destination-country chooser's "Try again" action - reloads for the current departure city, with `refresh`. */
  retryDestinationCountries() {
    this.#actions.getDestinationCountries(this.formGroup.controls.departureCityUID.value ?? undefined, true);
  }

  async retryDepartureCities() {
    this.#actions.getDepartureCities(undefined, true);

    const citiesState = await lastValueFrom(
      toObservable(this.departureCities, { injector: this.#injector }).pipe(
        filter((e) => !!e.loadState.status && e.loadState.status !== 'loading'),
        take(1)
      )
    );
    if (citiesState.loadState.status !== 'completed') return;

    const departureCity = await this.#settleDepartureCity();
    if (departureCity) await this.#reloadDestinationCountriesForDepartureCity(departureCity);
  }
  async initSearchData() {
    let searchByUrlData: ReturnType<typeof this.formGroup.getRawValue> | null =
      this.#selectors.searchByUrlData() as ReturnType<typeof this.formGroup.getRawValue>;
    if (searchByUrlData) {
      // R-SRF8b (docs#446): a party outside the Tours range (adults 1-10, children 0-10) is put back into it, with a warning
      const clamped = clampSearchLinkParty(searchByUrlData, {
        minAdults: this.params.minAdults,
        maxAdults: this.params.maxAdults,
        maxChildren: this.params.maxChildren
      });
      searchByUrlData = clamped.payload;
      if (clamped.adjusted) warnSearchLink(this.#injector, SEARCH_LINK_ADJUSTED_MESSAGE);

      if (searchByUrlData.nights || searchByUrlData.nightsTo || searchByUrlData.departureDateTo)
        this.onDateSearchModeChange('searchByPeriods');
      else this.onDateSearchModeChange('searchByDates');

      if (searchByUrlData.children && !searchByUrlData?.childrenAges?.length) {
        this.onAddChildren(searchByUrlData.children);
      }

      if (searchByUrlData.checkInDate && searchByUrlData.departureDate) {
        const sCheckInDate = format(searchByUrlData.checkInDate, environment.formats.date);
        const sDepartureDate = format(searchByUrlData.departureDate, environment.formats.date);
        if (sCheckInDate !== sDepartureDate) this.formGroup.controls.checkInCheckOutChange.setValue(true);
      } else if (searchByUrlData.checkOutDate && searchByUrlData.returnDate) {
        const sCheckOutDate = format(searchByUrlData.checkOutDate, environment.formats.date);
        const sReturnDate = format(searchByUrlData.returnDate, environment.formats.date);
        if (sCheckOutDate !== sReturnDate) this.formGroup.controls.checkInCheckOutChange.setValue(true);
      }
      this.#actions.updateFormValue({ path: this.ngxsForm, value: searchByUrlData });

      this.isSearchByModule = true;

      this.#loadingWindowService.show();
    }

    this.#actions.getDestinationCountries();
    this.#actions.getDepartureCities();

    let storedFormData = this.#selectors.searchFormData() as typeof this.formGroup.value;

    let isIntConvert = false;
    if (
      storedFormData?.departureCountryId ||
      storedFormData?.departureCityID ||
      storedFormData?.arrivalCountryId ||
      storedFormData?.hotelIds?.length
    ) {
      const convertedParams = await this.#convertIntToCodes({
        arrivalCountryId: storedFormData.arrivalCountryId,
        departureCityID: storedFormData.departureCityID,
        departureCountryId: storedFormData.departureCountryId,
        hotelIds: storedFormData.hotelIds,
        arrivalLocations: storedFormData.arrivalLocations ?? []
      });
      storedFormData = {
        ...storedFormData,
        ...convertedParams,
        // Reset Int values
        arrivalCountryId: null,
        departureCityID: null,
        departureCountryId: null,
        hotelIds: null
      };
      isIntConvert = true;
    }

    if (storedFormData) this.formGroup.patchValue({ ...storedFormData });

    // DEST-017: settle the departure city first (a restored URL/saved search wins), then reload the destinations sold
    // from it (package mode) before settling the arrival country - never open on a country the site does not offer.
    const initialDepartureCity = await this.#settleDepartureCity();
    if (initialDepartureCity) await this.#reloadDestinationCountriesForDepartureCity(initialDepartureCity);
    else await this.#settleDestinationCountry();

    //
    const formData = this.formGroup.getRawValue();
    if (storedFormData?.childrenAges?.length)
      for (const iterator of storedFormData.childrenAges)
        this.formGroup.controls.childrenAges.push(new FormControl<number | null>(iterator, Validators.required));

    if (
      (!isIntConvert && !storedFormData?.departureCountryId) ||
      (!isIntConvert && storedFormData?.departureCityID) ||
      (!isIntConvert && !storedFormData?.arrivalCountryId)
    )
      this.#actions.getAvailableAirlines({
        departureCityUid: formData?.departureCityUID ?? undefined,
        destinationCountryCode: formData?.arrivalCountryCode ?? undefined
      });

    if (!isIntConvert && !storedFormData?.arrivalCountryId)
      this.#actions.getArrivalDistricts({ countryCode: formData?.arrivalCountryCode ?? undefined }, true);

    if (!isIntConvert && !storedFormData?.hotelIds?.length)
      this.#actions.getHotels({ countryCode: formData?.arrivalCountryCode ?? undefined }, true);

    this.onDateSearchModeChange(this.searchMode() ?? 'searchByDates');

    this.observeMandatoryOptions().subscribe((isReady) => {
      // update hotel filters
      this.updateHotelFilters(true);
      this.calcDepartureAndReturnDaysCount();
      this.calcCheckInAndCheckOutDaysCount();

      this.isReady = isReady;
      if (!isReady) return;

      // Recover an arrival-country change made before the form was ready. The country selector is
      // interactive as soon as the small countries list loads (~1s before the large hotels list marks
      // the form ready), so a selection made in that window is dropped by the `if (!this.isReady)`
      // guard in onArrivalCountryChange — the dropdown shows the new country while districts/hotels
      // stay on the one initSearchData loaded. If they no longer match, reload for the selected
      // country now (a real Country object, so the null/popup branch is never taken).
      const selectedArrivalCountry = this.formGroup.controls.arrivalCountryCode.value;
      if (selectedArrivalCountry && selectedArrivalCountry !== this.storeArrivalCountryCode) {
        const country = this.#selectors
          .destinationCountries()
          .response?.find((c) => c.countryCode === selectedArrivalCountry);
        if (country) this.onArrivalCountryChange(country);
      }

      //Submit while SearchByUrl module exists
      if (this.isSearchByModule) {
        if (this.formGroup.controls.searchCurrency.value) {
          const currencyExists = this.#selectors
            .currencies()
            .some((e) => e.code === this.formGroup.controls.searchCurrency.value);
          if (currencyExists) this.#actions.setCurrency(this.formGroup.controls.searchCurrency.value);
        }

        setTimeout(async () => {
          if (searchByUrlData) await this.#validateSearchByUrlPayload(searchByUrlData);
          this.isSearchByModule = false;
          this.#actions.clearSearchByUrl();
          this.onSubmit(true);
          this.#actions.resultIsFromGeneratedUrl(true);
        }, 1000);
      }
    });
  }

  async #convertIntToCodes({
    departureCityID,
    departureCountryId,
    arrivalCountryId,
    hotelIds,
    arrivalLocations
  }: Pick<
    SearchTourPackagesRequest,
    'departureCityID' | 'departureCountryId' | 'arrivalCountryId' | 'hotelIds' | 'arrivalLocations'
  >): Promise<
    Pick<
      SearchTourPackagesRequest,
      'hotelCodes' | 'departureCityUID' | 'departureCountryCode' | 'arrivalCountryCode' | 'arrivalLocations'
    >
  > {
    const result: Pick<
      SearchTourPackagesRequest,
      'hotelCodes' | 'departureCityUID' | 'departureCountryCode' | 'arrivalCountryCode' | 'arrivalLocations'
    > = { hotelCodes: [], departureCityUID: null, departureCountryCode: null, arrivalCountryCode: null };
    //convert DepartureCountryId to DepartureCountryCode
    if (departureCountryId || departureCityID) {
      const data = await lastValueFrom(
        toObservable(this.departureCities, { injector: this.#injector }).pipe(
          filter((e) => e.loadState.status === 'completed'),
          take(1)
        )
      );
      const departureCitiesData = data?.response ?? [];

      for (const element of departureCitiesData) {
        if (departureCityID) {
          const departureCity = element.cities?.find((e) => e.cityID === departureCityID);
          if (departureCity?.countryCode && departureCity.cityUID) {
            result['departureCityUID'] = departureCity.cityUID;
            result['departureCountryCode'] = departureCity.countryCode;
            break;
          }
        } else {
          const departureCountryCode =
            element.cities?.find((e) => e.countryID === departureCountryId)?.countryCode ?? null;

          if (departureCountryCode) {
            result['departureCountryCode'] = departureCountryCode;
            result['departureCityUID'] = null;
            break;
          }
        }
      }
    }

    //Convert ArrivalCountryId to ArrivalCountryCode
    if (arrivalCountryId) {
      const data = await lastValueFrom(
        toObservable(this.countries, { injector: this.#injector }).pipe(
          filter((e) => e.loadState.status === 'completed'),
          take(1)
        )
      );

      const arrivalCountry = data.response?.find((e) => e.countryID === arrivalCountryId);

      if (arrivalCountry?.countryCode) {
        result['arrivalCountryCode'] = arrivalCountry.countryCode;
      }
    }

    this.#actions.getArrivalDistricts({ countryCode: result['arrivalCountryCode'] ?? undefined }, true);
    this.#actions.getAvailableAirlines(
      {
        departureCityUid: result['departureCityUID'] ?? undefined,
        destinationCountryCode: result['arrivalCountryCode'] ?? undefined
      },
      true
    );

    this.#actions.getHotels({ countryCode: result['arrivalCountryCode'] ?? undefined }, true);

    //Convert HotelIds to Codes
    if (hotelIds?.length) {
      const hotelsData = await lastValueFrom(
        toObservable(this.availableHotels, { injector: this.#injector }).pipe(
          filter((e) => e.loadState.status === 'completed'),
          take(1)
        )
      );
      if (hotelsData?.response?.length) {
        const hotelCodes: string[] = [];
        hotelIds.forEach((id) => {
          const hotelCode = hotelsData?.response?.find((e) => e.hotelID === id)?.hotelCode;
          if (hotelCode) hotelCodes.push(hotelCode);
        });

        result['hotelCodes'] = hotelCodes;
      }
    }

    //Convert ArrivalLocations Int values to Values
    const hasLocationsIntValue = arrivalLocations?.some((e) => e.valueInt && e.valueInt > 0);
    if (hasLocationsIntValue) {
      const locationsData = await lastValueFrom(
        toObservable(this.arrivalDistricts, { injector: this.#injector }).pipe(
          filter((e) => e.loadState.status === 'completed'),
          take(1)
        )
      );
      const _arrivalLocations = convertLocationsIntValuesToValues(arrivalLocations ?? [], locationsData.response ?? []);

      result['arrivalLocations'] = _arrivalLocations;
    }

    return result;
  }
  async #validateSearchByUrlPayload(payload: ReturnType<typeof this.formGroup.getRawValue>) {
    const undefinedLog: { prop: string; note: string; ovverridden: string }[] = [];
    const formGroup = this.formGroup;
    const requiredFields: {
      field: keyof ReturnType<typeof formGroup.getRawValue> | (keyof ReturnType<typeof formGroup.getRawValue>)[];
      defaultField?: keyof ReturnType<typeof formGroup.getRawValue>;
    }[] = [];
    const keysToIgnore: (keyof ReturnType<typeof formGroup.getRawValue>)[] = [
      'departureAndReturnDaysCount',
      'checkInAndCheckOutDaysCount',
      'checkInCheckOutChange',
      'checkInDateTo'
    ];

    // Helper for undefined check
    const checkUndefined = (
      fields: (keyof ReturnType<typeof formGroup.getRawValue>)[] | keyof ReturnType<typeof formGroup.getRawValue>,
      defaultField?: keyof ReturnType<typeof formGroup.getRawValue>
    ) => {
      if (typeof fields === 'string') {
        if (typeof payload[fields] === 'undefined') {
          undefinedLog.push({
            prop: fields,
            note: 'is mandatory',
            ovverridden: formGroup.controls[fields]?.value
          });
        }
      } else if (defaultField && fields.every((f) => typeof payload[f] === 'undefined')) {
        undefinedLog.push({
          prop: fields.join(' or '),
          note: 'is mandatory, used: ' + defaultField,
          ovverridden: formGroup.controls[defaultField]?.value
        });
      }
    };
    requiredFields.push(
      { field: ['departureCityUID', 'departureCityID'], defaultField: 'departureCityUID' },
      { field: ['arrivalCountryCode', 'arrivalCountryId'], defaultField: 'arrivalCountryCode' },
      { field: 'departureDate' }
    );
    const wrongParams: string[] = [];

    if (this.searchMode() === 'searchByDates') {
      keysToIgnore.push('nights', 'nightsTo', 'departureDateTo');
      requiredFields.push({ field: 'returnDate' });
    } else {
      keysToIgnore.push('checkInDate', 'checkOutDate');
      requiredFields.push({ field: 'nights' }, { field: 'nightsTo' }, { field: 'departureDateTo' });

      if (payload.checkInDate) {
        wrongParams.push('checkInDate is not supported in search by periods mode, use departureDate.');
      }
      if (payload.checkInDateTo) {
        wrongParams.push('checkInDateTo is not supported in search by periods mode, use departureDateTo.');
      }
    }
    requiredFields.forEach(({ field, defaultField }) => {
      checkUndefined(field, defaultField);
    });

    // Unknown keys check
    const formValues = formGroup.getRawValue();
    const formKeys = Object.keys(formValues).filter(
      (e) => !keysToIgnore.includes(e as keyof ReturnType<typeof formGroup.getRawValue>)
    );
    const unknownKeys = Object.keys(payload).filter((key) => !formKeys.includes(key));

    if (unknownKeys.length || undefinedLog.length || wrongParams.length || this.formGroup.invalid) {
      console.info(
        '%cNote: These messages can be ignored unless troubleshooting search payload issues.',
        'color: #2ecc71; font-size: 14px; font-weight: bold;'
      );

      console.groupCollapsed(
        '%cSearch Payload Validation Issues (%s Mode)',
        'color: #e67e22; font-size: 15px; font-weight: bold;',
        this.searchMode()
      );
      console.info(
        'Detected issues in the search payload may affect the results, as default values will be used for some properties.\n\n' +
          'Search mode depends on the following properties: "nights", "nightsTo", and "departureDateTo". ' +
          'If any of these are present, the system will use "searchByPeriods"; otherwise, it will use "searchByDates".\n\n' +
          'Also, please note that the system uses strings (UID or Code) instead of integers. The following properties will be automatically converted to the correct string-based format:\n' +
          '\t- "hotelIds" → "hotelCodes"\n' +
          '\t- "arrivalCountryId" → "arrivalCountryCode"\n' +
          '\t- "departureCountryId" → "departureCountryCode"\n' +
          '\t- "departureCityID" → "departureCityUID"\n' +
          '\t- "arrivalLocations.valueInt" → "arrivalLocations.value"\n\n' +
          'This will not affect the search results.'
      );
      if (unknownKeys.length) {
        console.group('%cUnsupported properties in payload:', 'color:#FF0000; font-size: 14px;');
        console.table(unknownKeys);
        console.groupEnd();
        console.groupCollapsed('Supported properties:');
        console.table(formKeys);
        console.groupEnd();
      }

      if (undefinedLog.length) {
        console.group('%cMissing or undefined properties:', 'color:#FF0000; font-size: 14px;');
        console.table(undefinedLog);
        console.groupEnd();
      }
      if (wrongParams.length) {
        console.group('%cIncompatible properties for current mode:', 'color:#da9c55; font-size: 14px;');
        wrongParams.forEach((e) => console.info(`%c${e}`, 'color:#FF0000; font-size: 14px;'));
        console.groupEnd();
      }

      // log required params for this search
      console.groupCollapsed('Required properties for this search:');
      const mandatoryLog: Record<string, string> = {};
      requiredFields.forEach((e) => {
        if (typeof e.field === 'string') {
          mandatoryLog[e.field] = typeof formValues[e.field];
        } else {
          e.field.forEach((f) => {
            mandatoryLog[f] = typeof formValues[f];
          });
        }
      });
      console.table(mandatoryLog);
      console.groupEnd();

      const currentPayload = deepClean(formValues);

      console.groupCollapsed('Properties used for search:');
      console.table(currentPayload);
      console.groupEnd();

      this.#loadingWindowService.hide();

      const message = this.formGroup.invalid
        ? 'The search form contains invalid fields. Please review all inputs.'
        : 'The search payload has missing or incorrect parameters. That may affect the search results.';
      const confirmation = this.#confirmService.confirm(
        {
          title: 'Warning',
          message
        },
        'warn',
        { primaryButton: { label: 'Continue' } }
      );
      console.groupEnd();

      if (!this.formGroup.invalid) {
        await lastValueFrom(confirmation);
        this.#loadingWindowService.show();
      }
    }
  }
  observeMandatoryOptions() {
    const mandatoryOptions: Observable<RequestStateModel>[] = [
      toObservable(this.countries, { injector: this.#injector }),
      toObservable(this.departureCities, { injector: this.#injector }),
      toObservable(this.airlines, { injector: this.#injector }),
      toObservable(this.arrivalDistricts, { injector: this.#injector }),
      toObservable(this.availableHotels, { injector: this.#injector })
    ];
    return combineLatest(
      mandatoryOptions.map((e) =>
        e.pipe(
          filter((e) => e.loadState.status === 'completed'),
          map((e) => e.loadState.status === 'completed'),
          take(1)
        )
      )
    ).pipe(map((result) => result.every((value) => value)));
  }
  onDepartureCityChange(departureCityUid?: string) {
    //reset selected airline

    if (departureCityUid === this.storeDepartureCityUID) return;
    // online-api#62: no city (the select's first empty value) or a city that is not offered (a saved default no longer sold, the cities still
    // loading) is not a choice - the opening settles the city and asks once; these asked airlines and destinations that were then aborted
    if (
      !departureCityUid ||
      !flattenDepartureCities(this.departureCities().response ?? []).some((c) => c.cityUID === departureCityUid)
    )
      return;
    this.storeDepartureCityUID = departureCityUid ?? null;

    this.formGroup.controls.airlineCode.setValue(null);

    this.#actions.getAvailableAirlines(
      {
        departureCityUid,
        destinationCountryCode: this.formGroup?.controls?.arrivalCountryCode?.value ?? undefined
      },
      true
    );

    // DEST-017: package mode only sells what this departure city offers - reload and settle the arrival country
    this.#reloadDestinationCountriesForDepartureCity(departureCityUid);
  }
  onArrivalCountryChange(country: Country | null) {
    if (!this.isReady) return;
    let countryCode = country?.countryCode;
    //reset selected cities & hotels
    if (!countryCode) {
      // R-MR14 (website-web#34, Amiran 09.10.2026): no outbound link from the Tours form - a cleared country keeps the last one
      countryCode = this.storeArrivalCountryCode ?? this.#selectors.activedDestinationCountry();
      this.formGroup.controls.arrivalCountryCode.setValue(countryCode);
      return;
    }
    if (!country?.sellCurrency) return;
    this.#actions.setSellCurrency('tours', country.sellCurrency);

    if (this.storeArrivalCountryCode === countryCode) return;

    this.storeArrivalCountryCode = countryCode ?? null;
    //hide direct flights only for specific arrival directions task: #REQ-10240
    if (TOUR_DIRECT_FLIGHT_CHECKBOX_HIDE_FOR_ARRIVAL_DIRECTIONS.includes(countryCode)) {
      this.hideDirectFlightsOnly.set(true);
      this.formGroup.controls.directFlightsOnly.setValue(null);
    } else if (this.hideDirectFlightsOnly) this.hideDirectFlightsOnly.set(false);
    this.formGroup.controls.arrivalLocations.setValue(null);
    this.formGroup.controls.hotelCodes.setValue(null);
    this.hotelsOptionFilterPredicate.update(() => null);

    this.#actions.getArrivalDistricts({ countryCode }, true);
    this.#actions.getHotels({ countryCode }, true);
    this.#actions.getAvailableAirlines(
      {
        departureCityUid: this.formGroup.controls.departureCityUID.value ?? undefined,
        destinationCountryCode: countryCode
      },
      true
    );
    //update check in and check out dates when moveCheckinDaysByCountryCode is defined
    if (typeof this.moveCheckinDaysByCountryCode !== 'undefined') this.updateCheckOutCheckInControls();

    this.clearSearchFormFilterControls();
  }
  clearSearchFormFilterControls() {
    this.formGroup.patchValue({
      mealPlans: null,
      hotelClasses: null,
      hotelTypes: null,
      hotelServices: null
    });
  }

  flightAvailabilityDateClassFromFn$: DynamicDateClassLoadFn = ({ month, year }) =>
    this.flightAvailabilityDateClassDataSource(month, year, true);
  flightAvailabilityDateClassToFn$: DynamicDateClassLoadFn = ({ month, year }) =>
    this.flightAvailabilityDateClassDataSource(month, year, false);

  getFlightAvailabilityClass(availableCount?: number) {
    if (availableCount && availableCount > environment.defaultFlightAvailabilityCount) return ['flight-available'];
    else if (availableCount && availableCount > 0 && availableCount <= environment.defaultFlightAvailabilityCount)
      return ['flight-fever'];
    else if (availableCount === 0) return ['flight-frequency'];
    return [''];
  }
  calcDepartureAndReturnDaysCount(isDepartureDateChange = false) {
    const departureDate = this.formGroup.controls.departureDate.value;
    let returnDate = this.formGroup.controls.returnDate.value;
    const departureAndReturnDaysCount = this.formGroup.controls.departureAndReturnDaysCount.value;

    if (
      departureDate &&
      isDepartureDateChange &&
      departureAndReturnDaysCount !== null &&
      departureAndReturnDaysCount > -1
    ) {
      returnDate = addDays(departureDate, departureAndReturnDaysCount);
      this.formGroup.controls.returnDate.setValue(returnDate);
    } else if (departureDate) {
      // R-SRF10 (website-web#168): an emptied Departure box is "still typing" - it must not wipe Nights, or the next typed date
      // took its nights from the old Return (yesterday -> 8 nights, Return kept)
      const difference = returnDate ? differenceInDays(returnDate, departureDate) : null;
      if (difference && difference < 0) {
        returnDate = addDays(departureDate, 7);
        this.formGroup.controls.returnDate.setValue(returnDate);
      }
      this.formGroup.controls.departureAndReturnDaysCount.setValue(
        returnDate ? differenceInDays(returnDate, departureDate) : null
      );
    }

    this.updateCheckOutCheckInDatesIfAble();
  }
  updateDatesByDepartureAndArrivalDaysCount() {
    const departureAndReturnDaysCount = this.formGroup.controls.departureAndReturnDaysCount.value;
    if (departureAndReturnDaysCount === null || departureAndReturnDaysCount < 0) return;

    let departureDate = this.formGroup.controls.departureDate.value;
    if (!departureDate) {
      departureDate = new Date();
      this.formGroup.controls.departureDate.setValue(departureDate);
    }

    this.formGroup.controls.returnDate.setValue(addDays(departureDate, departureAndReturnDaysCount));

    this.updateCheckOutCheckInDatesIfAble();
  }
  calcCheckInAndCheckOutDaysCount(isCheckInDateChange = false) {
    const checkInDate = this.formGroup.controls.checkInDate.value;
    let checkOutDate = this.formGroup.controls.checkOutDate.value;
    const checkInAndCheckOutDaysCount = this.formGroup.controls.checkInAndCheckOutDaysCount.value;

    if (
      checkInDate &&
      isCheckInDateChange &&
      checkInAndCheckOutDaysCount !== null &&
      checkInAndCheckOutDaysCount > -1
    ) {
      checkOutDate = addDays(checkInDate, checkInAndCheckOutDaysCount);
      this.formGroup.controls.checkOutDate.setValue(checkOutDate);
    } else {
      const difference = checkInDate && checkOutDate ? differenceInDays(checkOutDate, checkInDate) : null;
      if (difference && difference < 0 && checkInDate) {
        checkOutDate = addDays(checkInDate, 7);
        this.formGroup.controls.checkOutDate.setValue(checkOutDate);
      }
      this.formGroup.controls.checkInAndCheckOutDaysCount.setValue(
        checkInDate && checkOutDate ? differenceInDays(checkOutDate, checkInDate) : null
      );
    }
  }

  updateCheckInAndCheckOutDaysCount() {
    const checkInAndCheckOutDaysCount = this.formGroup.controls.checkInAndCheckOutDaysCount.value;
    if (checkInAndCheckOutDaysCount === null || checkInAndCheckOutDaysCount < 0) return;

    let checkInDate = this.formGroup.controls.checkInDate.value;
    if (!checkInDate) {
      checkInDate = new Date();
      this.formGroup.controls.checkInDate.setValue(checkInDate);
    }

    this.formGroup.controls.checkOutDate.setValue(addDays(checkInDate, checkInAndCheckOutDaysCount));
  }
  /*  */
  updateCheckOutCheckInControls() {
    if (this.formGroup.controls.checkInCheckOutChange.value) {
      this.formGroup.controls.checkInDate.enable();
      this.formGroup.controls.checkOutDate.enable();
      this.formGroup.controls.checkInDateTo.enable();
      this.formGroup.controls.checkInAndCheckOutDaysCount.enable();
    } else {
      this.formGroup.controls.checkInDate.disable();
      this.formGroup.controls.checkOutDate.disable();
      this.formGroup.controls.checkInDateTo.disable();

      this.formGroup.controls.checkInAndCheckOutDaysCount.disable();
      this.updateCheckOutCheckInDatesIfAble();
    }
    //
    if (this.searchMode() === 'searchByPeriods') this.formGroup.controls.checkInAndCheckOutDaysCount.disable();
  }
  updateCheckOutCheckInDatesIfAble() {
    const isSearchByDates = this.searchMode() === 'searchByDates';
    if (!this.formGroup.controls.checkInCheckOutChange.value) {
      let checkInDate = this.formGroup.controls.departureDate.value;
      //Move check in check out dates by arrival country code configuration
      const arrivalCountryCode = this.formGroup.controls.arrivalCountryCode.value;
      if (arrivalCountryCode && this.moveCheckinDaysByCountryCode?.[arrivalCountryCode] && isSearchByDates) {
        const moveCheckinDays = this.moveCheckinDaysByCountryCode[arrivalCountryCode] ?? 0;
        checkInDate = addDays(this.formGroup.controls.departureDate.value!, moveCheckinDays);
      }
      this.formGroup.controls.checkInDate.setValue(checkInDate);
      if (isSearchByDates) {
        this.formGroup.controls.checkOutDate.setValue(this.formGroup.controls.returnDate.value);
      } else this.formGroup.controls.checkInDateTo.setValue(this.formGroup.controls.departureDateTo.value);
    } else {
      this.formGroup.controls.checkInDate.updateValueAndValidity();
      this.formGroup.controls.checkOutDate.updateValueAndValidity();
      this.formGroup.controls.checkInDateTo.updateValueAndValidity();
      this.formGroup.controls.checkInAndCheckOutDaysCount.updateValueAndValidity();
    }
  }

  resetForm() {
    this.formGroup.reset({
      adults: 2,
      children: 0,
      childrenAges: [],
      departureCityUID: this.#selectors.defaultDepartureCityUID(),
      arrivalCountryCode: this.#selectors.activedDestinationCountry(),
      departureDate: this.today,
      returnDate: addDays(this.today, 7),
      departureAndReturnDaysCount: 7,
      directFlightsOnly: true,
      checkInDate: this.today,
      checkOutDate: addDays(this.today, 7),
      checkInAndCheckOutDaysCount: 7
    });
    this.#actions.updateFormValue({ path: this.ngxsForm, value: this.formGroup.getRawValue() });
    setTimeout(() => this.#actions.resetSearchResult(), 100);
    this.formGroup.controls.checkInCheckOutChange.setValue(false);
    this.isSubmited.set(false);
  }

  onDateSearchModeChange(mode: 'searchByDates' | 'searchByPeriods' | null) {
    if (this.searchMode() === mode) return;
    this.searchMode.set(mode);

    if (mode === 'searchByDates') {
      this.formGroup.controls.nights.setValue(null);
      this.formGroup.controls.nightsTo.setValue(null);
      this.formGroup.controls.departureDateTo.setValue(null);
      this.formGroup.controls.checkInDateTo.setValue(null);

      if (!this.formGroup.controls.departureDate.value) this.formGroup.controls.departureDate.setValue(this.today);

      this.formGroup.controls.returnDate.setValue(addDays(this.formGroup.controls.departureDate.value!, 7));
      this.formGroup.controls.checkOutDate.setValue(addDays(this.formGroup.controls.departureDate.value!, 7));

      // Enable Validators

      this.formGroup.controls.returnDate.addValidators(Validators.required);
      this.formGroup.controls.returnDate.updateValueAndValidity();
      this.formGroup.controls.checkOutDate.addValidators(Validators.required);
      this.formGroup.controls.checkOutDate.updateValueAndValidity();
      this.formGroup.controls.departureAndReturnDaysCount.addValidators(Validators.required);
      this.formGroup.controls.departureAndReturnDaysCount.updateValueAndValidity();
      this.formGroup.controls.checkInAndCheckOutDaysCount.addValidators(Validators.required);
      this.formGroup.controls.checkInAndCheckOutDaysCount.updateValueAndValidity();

      // Remove Validators
      this.formGroup.controls.departureDateTo.removeValidators(Validators.required);
      this.formGroup.controls.departureDateTo.updateValueAndValidity();
      this.formGroup.controls.checkInDateTo.removeValidators(Validators.required);
      this.formGroup.controls.checkInDateTo.updateValueAndValidity();
      //
      this.formGroup.updateValueAndValidity();
      this.calcDepartureAndReturnDaysCount();
      this.calcCheckInAndCheckOutDaysCount();
      this.formGroup.controls.checkInCheckOutChange.enable();
    } else {
      this.formGroup.controls.departureDate.setValue(this.today);

      this.formGroup.controls.nights.setValue(2);
      this.formGroup.controls.nightsTo.setValue(2);

      this.formGroup.controls.departureDateTo.setValue(this.today);
      this.formGroup.controls.checkInDateTo.setValue(this.today);

      //
      this.formGroup.controls.returnDate.setValue(null);
      this.formGroup.controls.checkOutDate.setValue(null);
      this.formGroup.controls.departureAndReturnDaysCount.setValue(null);
      this.formGroup.controls.checkInAndCheckOutDaysCount.setValue(null);

      // Enable Validators
      this.formGroup.controls.checkInDateTo.addValidators(Validators.required);
      this.formGroup.controls.checkInDateTo.updateValueAndValidity();
      this.formGroup.controls.departureDateTo.addValidators(Validators.required);
      this.formGroup.controls.departureDateTo.updateValueAndValidity();
      // Remove Validators
      this.formGroup.controls.returnDate.removeValidators(Validators.required);
      this.formGroup.controls.returnDate.updateValueAndValidity();
      this.formGroup.controls.checkOutDate.removeValidators(Validators.required);
      this.formGroup.controls.checkOutDate.updateValueAndValidity();
      this.formGroup.controls.departureAndReturnDaysCount.removeValidators(Validators.required);
      this.formGroup.controls.departureAndReturnDaysCount.updateValueAndValidity();
      this.formGroup.controls.checkInAndCheckOutDaysCount.removeValidators(Validators.required);
      this.formGroup.controls.checkInAndCheckOutDaysCount.updateValueAndValidity();
      this.formGroup.updateValueAndValidity();

      // Lock Check In Check Out Dates
      this.formGroup.controls.checkInCheckOutChange.setValue(false);
      this.formGroup.controls.checkInCheckOutChange.disable();
      this.updateCheckInAndCheckOutDaysCount();
    }
    // Update CheckIn and CheckOut Dates controls
    setTimeout(() => this.updateCheckOutCheckInControls(), 10);
  }

  updateCheckInDateTo() {
    const checkInDate = this.formGroup.controls.checkInDate.value;
    const checkInDateTo = this.formGroup.controls.checkInDateTo.value;
    if (!checkInDate || !checkInDateTo) {
      return;
    }

    const diff = differenceInDays(checkInDateTo, checkInDate);
    const nightsTo = this.formGroup.controls.nightsTo.value ?? 0;

    if (diff <= 2 || diff > nightsTo) this.formGroup.controls.checkInDateTo.setValue(checkInDate);
  }

  updateDepartureTo() {
    const departureDate = this.formGroup.controls.departureDate.value;
    const departureDateTo = this.formGroup.controls.departureDateTo.value;
    if (!departureDate || !departureDateTo) {
      return;
    }

    const diff = differenceInDays(departureDateTo, departureDate);

    // R-SRF12 (website-web#169): "to" may equal "from" (diff 0); runs on a typed "Departure to" too, so a put-back value
    // (diff 0, echoed by the date picker) must not be put back again
    if (diff < 0 || diff > 7) this.formGroup.controls.departureDateTo.setValue(departureDate);
    this.updateCheckOutCheckInDatesIfAble();
  }

  onNightsChange(change: 'nights' | 'nightsTo') {
    const nights = this.formGroup.controls.nights.value ?? 0;
    const nightsTo = this.formGroup.controls.nightsTo.value ?? 0;
    if (change === 'nights' && nights > nightsTo) this.formGroup.controls.nightsTo.setValue(nights);
    else if (change === 'nightsTo' && nightsTo < nights) this.formGroup.controls.nights.setValue(nightsTo);
  }

  flightAvailabilityDateClassDataSource(
    Month: number,
    Year: number,
    IsFrom: boolean
  ): Observable<DynamicDateClassItem[]> {
    const {
      airlineCode: airlineId,
      departureCityUID: fromCityId,
      arrivalCountryCode: toCountryId
    } = this.formGroup.getRawValue();

    const payload = {
      AirlineCode: airlineId,
      CityUID: fromCityId,
      CountryCode: toCountryId,
      IsFrom,
      Month,
      Year
    } as CatalogueGetFlightDateOptionsListParams;

    const identifier = window.btoa(JSON.stringify(payload));

    const flightAvailabilityByParams = this.#store.select(ToursState.flightAvailabilityByParams(identifier));

    return this.#actions
      .loadFlightDateOptions(payload, identifier)
      .pipe(switchMap(() => flightAvailabilityByParams))
      .pipe(
        map((e) => {
          return (
            e?.response?.availableDates?.map((el) => ({
              date: el.departureDate as Date,
              cssClass: this.getFlightAvailabilityClass(el.remainingTickets)
            })) ?? []
          );
        })
      );
  }
}
